import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/axios';

const TYPE_LABELS = {
  shortlisted: 'Shortlisted', viewed: 'Application viewed', screening: 'Under review',
  contacted: 'Contacted', selected: 'Selected', offer: 'Offer', offer_accepted: 'Offer accepted',
  rejected: 'Rejected', joined: 'Joined', completed: 'Completed', withdrawn: 'Withdrawn',
  on_hold: 'On hold', interview_round_1: 'Interview R1', interview_round_2: 'Interview R2',
  hr_round: 'HR round', interview_reminder_24h: 'Interview reminder 24h',
  interview_reminder_1h: 'Interview reminder 1h', interview_cancelled: 'Interview cancelled',
  interview_rescheduled: 'Interview rescheduled', daily_jobs: 'Daily Jobs For You',
  job_alert: 'Job alert', career_tip: 'Career tip', profile_reminder: 'Profile reminder',
  saved_job_reminder: 'Saved job reminder', reengagement_3d: 'Re-engagement (3d)',
  reengagement_7d: 'Re-engagement (7d)', admin_broadcast: 'Admin broadcast', new_applicant: 'New applicant (employer)',
};
const label = (t) => TYPE_LABELS[t] || String(t || '—').replace(/_/g, ' ');
const pct = (a, b) => (b > 0 ? ((a / b) * 100).toFixed(1) : '0.0');
const dt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// Colour group per notification type so rows are easy to scan
const typeTone = (t) => {
  if (t === 'admin_broadcast') return 'bg-pink-50 text-pink-600 border-pink-100';
  if (t === 'new_applicant') return 'bg-indigo-50 text-indigo-600 border-indigo-100';
  if (/^interview|^hr_round/.test(t || '')) return 'bg-blue-50 text-blue-600 border-blue-100';
  if (/reminder|reengagement/.test(t || '')) return 'bg-amber-50 text-amber-600 border-amber-100';
  if (['daily_jobs', 'job_alert', 'career_tip'].includes(t)) return 'bg-emerald-50 text-emerald-600 border-emerald-100';
  if (['rejected', 'withdrawn'].includes(t)) return 'bg-red-50 text-red-500 border-red-100';
  return 'bg-purple-50 text-purple-600 border-purple-100';
};

const FIELD = 'w-full border border-gray-200 rounded-full px-4 py-2 text-sm bg-white text-gray-700 hover:border-purple-300 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const SELECT = `${FIELD} appearance-none cursor-pointer pr-9`;
const CAPTION = 'block text-xs font-semibold text-gray-500 mb-1 ml-1';
const COUNT_BTN = 'min-w-[2.5rem] text-xs font-bold px-3 py-1 rounded-full bg-purple-50 text-[#7C3AED] hover:bg-purple-100 transition disabled:bg-transparent disabled:text-gray-300 disabled:cursor-default';

const EMPTY_FILTERS = { type: '', opened: '', read: '', role: '', date_from: '', date_to: '', q: '' };

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const presetRange = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));
  return { date_from: iso(from), date_to: iso(to) };
};
const DATE_PRESETS = [
  { label: 'Today', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
];

const compareSort = (av, bv, dir) => {
  const aEmpty = av === null || av === undefined || av === '';
  const bEmpty = bv === null || bv === undefined || bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const r =
    typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? r : -r;
};
const nextSort = (prev, key) =>
  prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' };

function SortTh({ label: text, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      title="Click to sort"
      className={`text-left px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap transition hover:text-[#DB2777] ${active ? 'text-[#DB2777]' : ''}`}
    >
      {text}
      <span className={`ml-1 ${active ? '' : 'opacity-40'}`}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

function SelectBox({ caption, value, onChange, children }) {
  return (
    <div>
      <label className={CAPTION}>{caption}</label>
      <div className="relative">
        <select value={value} onChange={onChange} className={SELECT}>{children}</select>
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-purple-500 text-[10px]">▼</span>
      </div>
    </div>
  );
}

function Avatar({ name }) {
  return (
    <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white flex items-center justify-center text-sm font-bold">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function ContactButtons({ phone }) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return <span className="text-xs text-gray-400">No mobile number</span>;
  const wa = `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-gray-700 tabular-nums">{phone}</p>
      <div className="flex gap-1.5">
        <a href={`tel:${digits}`}
          className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-xs font-semibold border bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100 transition">
          📞 Call
        </a>
        <a href={wa} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-xs font-semibold border bg-green-50 text-green-600 border-green-100 hover:bg-green-100 transition">
          💬 WhatsApp
        </a>
      </div>
    </div>
  );
}

function OpenedBadge({ at }) {
  return at ? (
    <div>
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#ECFDF5] text-[#10B981]">✓ Opened</span>
      <p className="text-[11px] text-gray-400 mt-1">{dt(at)}</p>
    </div>
  ) : (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Not opened</span>
  );
}

function Pager({ page, lastPage, onPage }) {
  const nums = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(lastPage, page + 2); p++) nums.push(p);
  const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border transition disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex items-center gap-1">
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
      {nums.map((p) => (
        <button
          key={p}
          onClick={() => onPage(p)}
          className={`${btn} ${p === page ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm' : 'bg-white hover:bg-purple-50'}`}
        >
          {p}
        </button>
      ))}
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page >= lastPage} onClick={() => onPage(page + 1)}>Next</button>
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page >= lastPage} onClick={() => onPage(lastPage)}>»</button>
    </div>
  );
}

function EmptyState({ icon, title, hint }) {
  return (
    <div className="py-14 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center text-2xl">{icon}</div>
      <p className="mt-3 font-semibold text-gray-700">{title}</p>
      {hint && <p className="text-sm text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function SkeletonRows({ cols, rows = 5 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i} className="border-t border-gray-50">
      {Array.from({ length: cols }).map((__, c) => (
        <td key={c} className="px-4 py-4"><div className="h-4 rounded-full bg-gray-100 animate-pulse" style={{ width: `${55 + ((i + c) % 4) * 10}%` }} /></td>
      ))}
    </tr>
  ));
}

/* ---------- Popup: who was pushed / who opened ---------- */
function DetailsModal({ info, filters, onClose }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sort, setSort] = useState({ key: '', dir: 'desc' });

  const onSort = (key) => { setSort((p) => nextSort(p, key)); setPage(1); };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr('');
      try {
        const res = await api.get('/admin/notification-report', {
          params: {
            type: info.type,
            opened: info.metric === 'opened' ? 'yes' : undefined,
            role: filters.role || undefined,
            date_from: filters.date_from || undefined,
            date_to: filters.date_to || undefined,
            sort_by: sort.key || undefined,
            sort_dir: sort.key ? sort.dir : undefined,
            page,
            per_page: 25,
          },
        });
        if (!alive) return;
        setRows(res.data.data || []);
        setLastPage(res.data.last_page || 1);
        setTotal(res.data.total || 0);
      } catch (e) {
        if (alive) setErr(e.response?.data?.message || 'Could not load the user list. Close this window and try again.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [info, page, sort, filters.role, filters.date_from, filters.date_to]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold text-white">{label(info.type)}</h3>
            <p className="text-purple-100 text-xs mt-0.5">
              {info.metric === 'pushed' ? 'Users who received this push' : 'Users who tapped this push'} · {total} total
            </p>
          </div>
          <button onClick={onClose} className="text-purple-100 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="overflow-auto">
          {err && <p className="text-sm text-red-600 px-6 pt-4">{err}</p>}
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                {[['Name', 'user'], ['Email', 'email'], ['Mobile', 'mobile'], ['Sent at', 'sent_at'], ['Opened', 'opened_at']].map(([h, k]) => (
                  <SortTh key={k} label={h} sortKey={k} sort={sort} onSort={onSort} />
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={5} />
              ) : rows.length === 0 ? (
                <tr><td colSpan={5}><EmptyState icon="👥" title="No users found" hint="Try changing the filters on the report page." /></td></tr>
              ) : rows.map((n) => (
                <tr key={n.id} className="border-t border-gray-50 hover:bg-purple-50/60 transition-colors align-top">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={n.user?.name} />
                      <span className="font-medium text-gray-800">{n.user?.name || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 break-all">{n.user?.email || '—'}</td>
                  <td className="px-4 py-3"><ContactButtons phone={n.user?.phone} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{dt(n.sent_at)}</td>
                  <td className="px-4 py-3"><OpenedBadge at={n.opened_at} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50 text-sm text-gray-500 shrink-0">
          <span>Page {page} of {lastPage}</span>
          <Pager page={page} lastPage={lastPage} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}

/* ---------- Main page ---------- */
export default function NotificationReportPanel() {
  const [f, setF] = useState(EMPTY_FILTERS);
  const [summary, setSummary] = useState({ types: [], totals: { total: 0, sent: 0, opened: 0 } });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null);
  const [sort, setSort] = useState({ key: '', dir: 'desc' });
  const [typeSort, setTypeSort] = useState({ key: '', dir: 'desc' });

  const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e.target.value })); setPage(1); };
  const patch = (obj) => { setF((x) => ({ ...x, ...obj })); setPage(1); };
  const params = (extra = {}) => ({ ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), ...extra });

  const onSort = (key) => { setSort((p) => nextSort(p, key)); setPage(1); };
  const onTypeSort = (key) => setTypeSort((p) => nextSort(p, key));
  const clearFilters = () => { setF(EMPTY_FILTERS); setPage(1); };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [s, l] = await Promise.all([
        api.get('/admin/notification-report/summary', { params: params() }),
        api.get('/admin/notification-report', {
          params: params({ page, per_page: perPage, ...(sort.key ? { sort_by: sort.key, sort_dir: sort.dir } : {}) }),
        }),
      ]);
      setSummary(s.data);
      setRows(l.data.data || []);
      setLastPage(l.data.last_page || 1);
      setTotal(l.data.total || 0);
      setLoadedOnce(true);
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not load the report. Check your connection and try again.');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, page, perPage, sort]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const t = summary.totals;
  const openRate = Number(pct(t.opened, t.sent));

  const sortedTypes = useMemo(() => {
    if (!typeSort.key) return summary.types;
    const val = (x) => {
      switch (typeSort.key) {
        case 'type': return label(x.type);
        case 'sent': return Number(x.sent) || 0;
        case 'opened': return Number(x.opened) || 0;
        case 'rate': return Number(x.sent) > 0 ? Number(x.opened) / Number(x.sent) : 0;
        default: return null;
      }
    };
    return summary.types.slice().sort((a, b) => compareSort(val(a), val(b), typeSort.dir));
  }, [summary.types, typeSort]);

  // Removable chips for the filters that are switched on
  const chips = [
    f.q && { k: 'q', text: `Search: ${f.q}`, clear: { q: '' } },
    f.type && { k: 'type', text: label(f.type), clear: { type: '' } },
    f.read && { k: 'read', text: f.read === 'yes' ? 'Read' : 'Unread', clear: { read: '' } },
    f.opened && { k: 'opened', text: f.opened === 'yes' ? 'Opened' : 'Not opened', clear: { opened: '' } },
    f.role && { k: 'role', text: f.role === 'employer' ? 'Employers' : 'Job seekers', clear: { role: '' } },
    f.date_from && { k: 'from', text: `From ${f.date_from}`, clear: { date_from: '' } },
    f.date_to && { k: 'to', text: `To ${f.date_to}`, clear: { date_to: '' } },
  ].filter(Boolean);

  const activePreset = DATE_PRESETS.find((p) => {
    const r = presetRange(p.days);
    return f.date_from === r.date_from && f.date_to === r.date_to;
  })?.days;

  const cards = [
    { title: 'Total created', value: t.total, hint: 'Push notifications created', icon: '🔔', tone: 'from-[#7C3AED] to-[#A78BFA]' },
    { title: 'Delivered to phones', value: t.sent, hint: 'Sent through the mobile app', icon: '📲', tone: 'from-[#10B981] to-[#34D399]' },
    { title: 'Opened', value: t.opened, hint: 'Tapped by the user', icon: '👆', tone: 'from-[#F59E0B] to-[#FBBF24]' },
  ];

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.title} className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.tone} text-white flex items-center justify-center text-xl shadow-sm shrink-0`}>{c.icon}</div>
            <div className="min-w-0">
              <p className="text-2xl font-extrabold text-[#0D1B3D] leading-tight">{Number(c.value).toLocaleString('en-IN')}</p>
              <p className="text-sm font-semibold text-gray-600">{c.title}</p>
              <p className="text-xs text-gray-400 truncate">{c.hint}</p>
            </div>
          </div>
        ))}
        <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-5">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-semibold text-gray-600">Open rate</p>
            <p className="text-2xl font-extrabold text-[#DB2777]">{openRate}%</p>
          </div>
          <div className="mt-3 h-2.5 rounded-full bg-pink-50 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#DB2777] transition-all duration-500" style={{ width: `${Math.min(100, openRate)}%` }} />
          </div>
          <p className="text-xs text-gray-400 mt-2">{t.opened} opened out of {t.sent} delivered</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#0D1B3D]">Filter notifications</h3>
          {chips.length > 0 && (
            <button type="button" onClick={clearFilters}
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition">
              ✕ Clear all
            </button>
          )}
        </div>

        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
          <input className={`${FIELD} pl-10`} placeholder="Search by user name or email" value={f.q} onChange={set('q')} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <SelectBox caption="Notification type" value={f.type} onChange={set('type')}>
            <option value="">All types</option>
            {summary.types.map((x) => <option key={x.type} value={x.type}>{label(x.type)}</option>)}
          </SelectBox>
          <SelectBox caption="User group" value={f.role} onChange={set('role')}>
            <option value="">Everyone</option>
            <option value="job_seeker">Job seekers</option>
            <option value="employer">Employers</option>
          </SelectBox>
          <SelectBox caption="Opened (tapped)" value={f.opened} onChange={set('opened')}>
            <option value="">All</option>
            <option value="yes">Opened</option>
            <option value="no">Not opened</option>
          </SelectBox>
          <SelectBox caption="Read" value={f.read} onChange={set('read')}>
            <option value="">All</option>
            <option value="yes">Read</option>
            <option value="no">Unread</option>
          </SelectBox>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={CAPTION}>Created from</label>
            <input type="date" className={`${FIELD} w-44`} value={f.date_from} onChange={set('date_from')} />
          </div>
          <div>
            <label className={CAPTION}>Created to</label>
            <input type="date" className={`${FIELD} w-44`} value={f.date_to} onChange={set('date_to')} />
          </div>
          <div className="flex flex-wrap gap-2 pb-0.5">
            {DATE_PRESETS.map((p) => (
              <button key={p.days} type="button" onClick={() => patch(presetRange(p.days))}
                className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition ${
                  activePreset === p.days
                    ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
                    : 'bg-white text-purple-700 border-purple-200 hover:border-purple-400'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {chips.map((c) => (
              <span key={c.k} className="inline-flex items-center gap-1.5 text-xs font-medium pl-3 pr-1.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                {c.text}
                <button type="button" onClick={() => patch(c.clear)} className="h-4 w-4 rounded-full hover:bg-purple-200 leading-none" aria-label="Remove filter">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {err && (
        <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-100 text-red-700 rounded-xl px-4 py-3 text-sm">
          <span>{err}</span>
          <button type="button" onClick={load} className="font-semibold underline">Retry</button>
        </div>
      )}

      {/* By type */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-[#0D1B3D]">Performance by notification type</h3>
          <p className="text-xs text-gray-400 mt-0.5">Click a delivered or opened number to see which users it covers.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                {[['Type', 'type'], ['Delivered', 'sent'], ['Opened', 'opened'], ['Open rate', 'rate']].map(([h, k]) => (
                  <SortTh key={k} label={h} sortKey={k} sort={typeSort} onSort={onTypeSort} />
                ))}
              </tr>
            </thead>
            <tbody>
              {!loadedOnce ? (
                <SkeletonRows cols={4} rows={3} />
              ) : sortedTypes.length === 0 ? (
                <tr><td colSpan={4}><EmptyState icon="📭" title="No notifications match these filters" hint="Clear a filter or widen the date range." /></td></tr>
              ) : sortedTypes.map((x) => {
                const rate = Number(pct(x.opened, x.sent));
                return (
                  <tr key={x.type} className="border-t border-gray-50 hover:bg-purple-50/60 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${typeTone(x.type)}`}>{label(x.type)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button type="button" className={COUNT_BTN} disabled={!Number(x.sent)} onClick={() => setModal({ type: x.type, metric: 'pushed' })}>{x.sent}</button>
                    </td>
                    <td className="px-4 py-3">
                      <button type="button" className={COUNT_BTN} disabled={!Number(x.opened)} onClick={() => setModal({ type: x.type, metric: 'opened' })}>{x.opened}</button>
                    </td>
                    <td className="px-4 py-3 w-64">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#DB2777]" style={{ width: `${Math.min(100, rate)}%` }} />
                        </div>
                        <span className="w-14 text-right text-xs font-bold text-[#7C3AED] tabular-nums">{rate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* All notifications */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-[#0D1B3D]">Push notifications</h3>
            <p className="text-xs text-gray-400 mt-0.5">{total.toLocaleString('en-IN')} found. Click a column heading to sort.</p>
          </div>
          {loading && loadedOnce && <span className="text-xs font-medium text-purple-500 animate-pulse">Updating…</span>}
        </div>

        <div className={`overflow-x-auto transition-opacity ${loading && loadedOnce ? 'opacity-60' : ''}`}>
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                {[
                  ['User', 'user'], ['Mobile', 'mobile'], ['Type', 'type'], ['Message', 'message'],
                  ['Sent at', 'sent_at'], ['Opened', 'opened_at'], ['Created', 'created_at'],
                ].map(([h, k]) => <SortTh key={k} label={h} sortKey={k} sort={sort} onSort={onSort} />)}
              </tr>
            </thead>
            <tbody>
              {!loadedOnce ? (
                <SkeletonRows cols={7} />
              ) : rows.length === 0 ? (
                <tr><td colSpan={7}><EmptyState icon="🔕" title="No notifications found" hint="Try clearing the filters or choosing a longer date range." /></td></tr>
              ) : rows.map((n) => (
                <tr key={n.id} className="border-t border-gray-50 hover:bg-purple-50/60 transition-colors align-top">
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <Avatar name={n.user?.name} />
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800">{n.user?.name || '—'}</p>
                        <p className="text-xs text-gray-500 break-all">{n.user?.email || '—'}</p>
                        {n.user?.role && (
                          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500 capitalize">
                            {n.user.role.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><ContactButtons phone={n.user?.phone} /></td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${typeTone(n.type)}`}>{label(n.type)}</span>
                  </td>
                  <td className="px-4 py-3 max-w-[280px]">
                    <p className="text-gray-600 leading-snug break-words" title={n.message}
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {n.message || '—'}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{dt(n.sent_at)}</td>
                  <td className="px-4 py-3"><OpenedBadge at={n.opened_at} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{dt(n.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-3">
            <span>Page {page} of {lastPage}</span>
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
          <Pager page={page} lastPage={lastPage} onPage={setPage} />
        </div>
      </div>

      {modal && <DetailsModal info={modal} filters={f} onClose={() => setModal(null)} />}
    </div>
  );
}